import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useStoreActions, useStoreState } from 'easy-peasy';

import client from '../../utils/apolloClient';
import { Input } from '../elements';

const searchPlacesQuery = gql`
  query searchPlacesQuery($input: SearchInput) {
    search_places(input: $input) {
      total_pages
      total_size
      place_listing {
        place_id
        status
        name
        address_line_1
        address_line_2
        city
        state
        country
        post_code
        location
        timezone
      }
    }
  }
`;

const fetchPlaces = async ({ input }) => {
  try {
    const data = await client.clientPrivate.query({
      client: client.clientPrivate,
      query: searchPlacesQuery,
      variables: {
        input,
      },
      fetchPolicy: 'network-only',
    });

    if (data?.data?.search_places?.place_listing?.[0]) {
      return data.data.search_places.place_listing;
    }

    return [];
  } catch {
    return [];
  }
};

const ResultsList = styled.ul`
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
`;

const ResultItem = styled.li`
  padding: 8px;
  cursor: pointer;
  color: black;

  &:hover,
  &.active {
    background: #00aeef;
    color: #f6f6f6;
  }
`;

const PlaceSearchInput = () => {
  const { userId } = useStoreState(state => state.auth);
  const { setDefaultPlaceId } = useStoreActions(state => state.menu);

  const { defaultPlace } = useStoreState(state => state.menu);

  const [query, setQuery] = useState(() => defaultPlace?.name || '');

  const [collapsed, setCollapsed] = useState(false);
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState([]);

  const debounceRef = useRef(null);

  const searchPlacesQueryInputFunc = value => ({
    user_id: userId,
    from: 0,
    size: 10,
    filter: {
      place_filter: {
        status: 'ACTIVE',
        place_name: value,
        has_service_type_setting: true,
        listing_status: 'APPROVED',
        claim_status: 'CLAIMED',
      },
      service_type_setting_filter: {
        status: 'ACTIVE',
      },
    },
  });

  return (
    <>
      <Input
        label="Place Name"
        type="text"
        value={query}
        onChange={e => {
          const { value } = e.target;
          setCollapsed(false);
          setTouched(true);
          setQuery(value);
          setLoading(true);
          clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(async () => {
            const temp = await fetchPlaces({
              input: searchPlacesQueryInputFunc(value),
            });
            setPlaces(temp);
            setLoading(false);
          }, 300);
        }}
        key={defaultPlace}
        placeholder="Search places..."
      />
      {touched && (
        <>
          {loading ? (
            <ResultsList>
              <ResultItem>loading...</ResultItem>
            </ResultsList>
          ) : (
            <ResultsList>
              {!collapsed && (
                <>
                  {places.map(place => (
                    <ResultItem
                      key={place.place_id}
                      onClick={() => {
                        setTouched(false);
                        setCollapsed(true);
                        setDefaultPlaceId(place.place_id);
                        setQuery(place.name);
                      }}
                    >
                      {place.name}
                      <div style={{ fontSize: '0.8em', opacity: 0.8 }}>
                        {[
                          place.address_line_1,
                          place.city,
                          place.state,
                          place.country,
                          place.post_code,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </ResultItem>
                  ))}
                  {places.length === 0 && <ResultItem>No results</ResultItem>}
                </>
              )}
            </ResultsList>
          )}
        </>
      )}
    </>
  );
};

export default PlaceSearchInput;
